import { ButtonHTMLAttributes, ReactNode, useState } from "react";
import { RequestState } from "../../constants/request-state";
import { Note, NoteReply, SaveNoteReplayModel } from "../../types/note/Note";
import { isRequesting, isRequestSuccess } from "../../utils/request-state.utils";
import { Preloader } from "../common/Preloader";
import Popup from "../controls/Popup";
import { PopupBody } from "../controls/PopupBody";
import { PopupFooter } from "../controls/PopupFooter";
import { EditNote, NoteContent } from "./EditNote";
import { NoteView } from "./NoteView";
import { NoteList } from "./NoteList";
import { Confirm } from "../alerts/Confirm";
import IconSVG from "../../styles/svg-icons";
import { NotesRequestState } from "./state/notes-request-state";
import { compareDatesDesc } from "../../utils/compare.utils";
import { stringUtils } from "../../utils/string.utils";
import { user } from "../../user/user";

interface Props {
    requestState: RequestState
    noteSaveRequestState: NotesRequestState
    noteReplySaveRequestState: NotesRequestState
    noteDeleteRequestState: NotesRequestState
    noteReplyDeleteRequestState: NotesRequestState
    myNotesDeleteRequestState: RequestState
    notes: Note[]
    title: ReactNode
    renderCustomHeaderContent?: (note: Note) => React.ReactNode
    onNoteSave: (note: NoteContent, original?: Note) => Promise<boolean>
    onNoteReplySave: (reply: SaveNoteReplayModel) => Promise<boolean>
    onNoteDelete: (note: Note) => void
    onNoteReplyDelete: (reply: NoteReply) => void
    onMyNotesDelete: () => void
    onClose: () => void
}

export function NotesPopup({
    title, requestState, noteSaveRequestState, noteReplySaveRequestState, myNotesDeleteRequestState,
    noteDeleteRequestState, noteReplyDeleteRequestState, notes, renderCustomHeaderContent,
    onMyNotesDelete: onDeleteMyNotes, onNoteSave, onNoteReplySave, onNoteDelete, onNoteReplyDelete, onClose }: Props) {

    const hasMyNotes = notes.some(n =>
        stringUtils.equalsIgnoringCase(n.user.email, user.current()?.email ?? ''));

    return (
        <Popup title={title} onClose={onClose} modalClass="modal-notes">
            <PopupBody>
                <Preloader inProgress={!isRequestSuccess(requestState) || isRequesting(myNotesDeleteRequestState)}>
                    <NoteList>
                        {[...notes]
                            .sort((a, b) => compareDatesDesc(a.submittedDate, b.submittedDate))
                            .map(n =>
                                <NoteView
                                    key={n.id}
                                    note={n}
                                    noteSaveRequestState={noteSaveRequestState[n.id]}
                                    noteReplySaveRequestState={noteReplySaveRequestState}
                                    noteDeleteRequestState={noteDeleteRequestState[n.id]}
                                    noteReplyDeleteRequestState={noteReplyDeleteRequestState}
                                    renderCustomHeaderContent={renderCustomHeaderContent}
                                    onNoteSave={onNoteSave}
                                    onNoteReplySave={onNoteReplySave}
                                    onNoteDelete={onNoteDelete}
                                    onNoteReplyDelete={onNoteReplyDelete}
                                />
                            )}
                    </NoteList>
                </Preloader>
            </PopupBody>
            <PopupFooter>
                <EditNote
                    requestState={noteSaveRequestState[0]}
                    renderCustomButtons={() =>
                        hasMyNotes &&
                        <DeleteMyNotesButton
                            onClick={onDeleteMyNotes}
                        />}
                    onSave={onNoteSave}
                    onCancel={onClose}
                />
            </PopupFooter>
        </Popup>
    )
}

function DeleteMyNotesButton(props: ButtonHTMLAttributes<HTMLButtonElement>) {
    const [isConfirm, setConfirm] = useState(false)

    return (
        <>
            <button
                type="button"
                className="btn-link btn-danger"
                {...props}
                onClick={() => setConfirm(true)}
            >
                <IconSVG
                    name="basket"
                    width={16}
                    height={16}
                />
                Delete all my notes
            </button>
            {isConfirm &&
                <Confirm
                    renderInBody
                    text="Are you sure you want to delete all your notes?"
                    onConfirm={(e: React.MouseEvent<HTMLButtonElement>) => {
                        props.onClick?.(e);
                        setConfirm(false);
                    }}
                    onCancel={() => setConfirm(false)}
                />
            }
        </>
    );

}